import { Fragment, useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { InvoiceDetailLink } from "@/components/commissions/InvoiceDetailLink";
import { QboMvMismatchBadge } from "@/components/commissions/QboMvMismatchBadge";
import { EndUserDetailLink } from "@/components/inside-sales/EndUserDetailLink";
import { RepAgencyDetailLink } from "@/components/inside-sales/RepAgencyDetailLink";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input, Label } from "@/components/ui/Input";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp, HoverHintTrigger } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { cn } from "@/lib/utils";
import { CommissionRunWorkflowStatus } from "./CommissionRunWorkflowStatus";
import { isCommissionRunFullySignedOff } from "./commissionRunWorkflow";
import {
  approveCommissionRunRole,
  generateCommissionRunVendorInvoices,
  getCommissionBacktest,
  getCommissionRun,
  getCommissionRules,
  listCommissionRunExcludedItems,
  listCommissionRunLines,
  lockCommissionRun,
  money,
  sendCommissionRunStatements,
  sendCommissionRunStatementPreview,
  listCommissionStatementPreviewUsers,
  commissionsRulesQueryKey,
  type CommissionRunLineRow,
} from "./commissionsApi";
import { runLineInvoicePeekFromRow } from "./invoiceDetailPeek";
import {
  commissionRunMonthKey,
  commissionRunTitle,
  formatCommissionRunMonthLong,
} from "./commissionRunMonth";

const COMMISSION_LINE_SOURCE_KINDS = new Set(["invoice", "manual", "stocking_resale"]);
const BONUS_LINE_SOURCE_KINDS = new Set(["tier_bonus", "new_end_user_bonus"]);

const RUN_LINES_HEADER_CELL =
  "sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm";

const COMMISSION_KIND_OPTIONS = [
  { value: "invoice", label: "Invoice" },
  { value: "manual", label: "Manual" },
  { value: "stocking_resale", label: "Stocking resale" },
] as const;

const BONUS_KIND_OPTIONS = [
  { value: "tier_bonus", label: "Tier bonus" },
  { value: "new_end_user_bonus", label: "New end user bonus" },
] as const;

type RunLineSortKey =
  | "rep"
  | "kind"
  | "end_user"
  | "invoice"
  | "basis"
  | "rate"
  | "commission";

function isReplessCommissionLine(line: CommissionRunLineRow): boolean {
  return (
    line.source_kind === "invoice" &&
    line.rep_agency_id == null &&
    (line.rep_agency_name?.toUpperCase() === "N/A" || Number(line.commission_amount) === 0)
  );
}

function sumLineCommissionAmount(lines: readonly CommissionRunLineRow[]): number {
  return lines.reduce((sum, line) => {
    const amount =
      typeof line.commission_amount === "number"
        ? line.commission_amount
        : Number.parseFloat(String(line.commission_amount));
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

function sumLineBasisAmount(lines: readonly CommissionRunLineRow[]): number {
  return lines.reduce((sum, line) => {
    const amount =
      typeof line.basis_amount === "number"
        ? line.basis_amount
        : Number.parseFloat(String(line.basis_amount));
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
}

function repAgencyGroupLabel(line: CommissionRunLineRow): string {
  const name = line.rep_agency_name?.trim();
  if (name == null || name === "") {
    return "—";
  }
  return name;
}

type RepAgencyLineGroup = {
  label: string;
  repAgencyId: number | null;
  lines: CommissionRunLineRow[];
};

const RUN_LINE_SUBTOTAL_ROW_CLASS =
  "border-b border-slate-200 bg-slate-50 font-semibold text-slate-900";

type BacktestDiffRow = Awaited<ReturnType<typeof getCommissionBacktest>>["diffs"][number];

const BACKTEST_DIFF_COLUMNS: HubDataTableColumn<BacktestDiffRow>[] = [
  {
    id: "rep",
    header: "Rep",
    sortValue: (row) => row.repAgencyName,
    cell: (row) => row.repAgencyName,
  },
  {
    id: "invoice",
    header: "Invoice",
    overflow: "nowrap",
    sortValue: (row) => row.invoiceNumber ?? "",
    cell: (row) => row.invoiceNumber ?? "—",
  },
  {
    id: "sheet",
    header: "Sheet",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.sheetAmount,
    cell: (row) => money(row.sheetAmount),
  },
  {
    id: "hub",
    header: "Hub",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.hubAmount,
    cell: (row) => money(row.hubAmount),
  },
  {
    id: "delta",
    header: "Delta",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.delta,
    cell: (row) => money(row.delta),
  },
  {
    id: "reason",
    header: "Reason",
    sortValue: (row) => row.reason,
    cell: (row) => row.reason,
  },
];

function compareRunLines(
  left: CommissionRunLineRow,
  right: CommissionRunLineRow,
  sortKey: RunLineSortKey,
): number {
  switch (sortKey) {
    case "rep":
      return (
        compareNullableStrings(left.invoice_number, right.invoice_number) ||
        left.id - right.id
      );
    case "kind":
      return compareNullableStrings(left.source_kind, right.source_kind);
    case "end_user":
      return compareNullableStrings(left.end_user_name, right.end_user_name);
    case "invoice":
      return compareNullableStrings(left.invoice_number, right.invoice_number);
    case "basis":
      return compareNullableNumbers(
        parseLineNumber(left.basis_amount),
        parseLineNumber(right.basis_amount),
      );
    case "rate":
      return compareNullableNumbers(
        parseLineNumber(left.effective_rate),
        parseLineNumber(right.effective_rate),
      );
    case "commission":
      return compareNullableNumbers(
        parseLineNumber(left.commission_amount),
        parseLineNumber(right.commission_amount),
      );
    default:
      return 0;
  }
}

function filterTogglePillClass(active: boolean): string {
  return cn(
    "rounded-full border px-3 py-1 text-xs font-medium transition",
    active
      ? "border-brand-500 bg-brand-50 text-brand-800"
      : "border-slate-300 bg-white text-slate-500 hover:border-slate-400",
  );
}

function compareNullableStrings(left: string | null | undefined, right: string | null | undefined): number {
  const leftText = left?.trim() ?? "";
  const rightText = right?.trim() ?? "";
  if (leftText === "" && rightText === "") {
    return 0;
  }
  if (leftText === "") {
    return 1;
  }
  if (rightText === "") {
    return -1;
  }
  return leftText.localeCompare(rightText, undefined, { sensitivity: "base" });
}

function compareNullableNumbers(left: number | null, right: number | null): number {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  return left - right;
}

function parseLineNumber(value: string | number | null | undefined): number | null {
  if (value == null || value === "") {
    return null;
  }
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function lineMatchesSearch(line: CommissionRunLineRow, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === "") {
    return true;
  }
  const haystack = [
    line.rep_agency_name,
    line.end_user_name,
    line.invoice_number,
    line.source_kind,
  ]
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

function KindCountPills({
  title,
  options,
  counts,
  selected,
  onToggle,
  onReset,
  showReset,
}: {
  title: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  counts: Partial<Record<string, number>>;
  selected: Set<string>;
  onToggle: (kind: string) => void;
  onReset: () => void;
  showReset: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex min-h-8 flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onReset}
          className={cn(!showReset && "pointer-events-none invisible")}
          tabIndex={showReset ? 0 : -1}
          aria-hidden={!showReset}
        >
          Reset filters
        </Button>
      </div>
      <div className="flex min-h-[2.25rem] flex-wrap content-start gap-1">
        {options.map((option) => {
          const active = selected.has(option.value);
          const count = counts[option.value] ?? 0;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => onToggle(option.value)}
              className={filterTogglePillClass(active)}
            >
              {option.label}: {count.toLocaleString()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CommissionRunLinesTable({
  title,
  lines,
  kindOptions,
  searchInputId,
}: {
  title: string;
  lines: readonly CommissionRunLineRow[];
  kindOptions: ReadonlyArray<{ value: string; label: string }>;
  searchInputId: string;
}) {
  const defaultKinds = useMemo(() => new Set(kindOptions.map((option) => option.value)), [kindOptions]);
  const [kindFilter, setKindFilter] = useState<Set<string>>(() => new Set(defaultKinds));
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<RunLineSortKey>("rep");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const kindCounts = useMemo(() => {
    const counts: Partial<Record<string, number>> = {};
    for (const line of lines) {
      counts[line.source_kind] = (counts[line.source_kind] ?? 0) + 1;
    }
    return counts;
  }, [lines]);

  const kindFilterAtDefault = useMemo(() => {
    if (kindFilter.size !== defaultKinds.size) {
      return false;
    }
    for (const kind of defaultKinds) {
      if (!kindFilter.has(kind)) {
        return false;
      }
    }
    return true;
  }, [defaultKinds, kindFilter]);

  const toggleKindFilter = useCallback((kind: string): void => {
    setKindFilter((current) => {
      const next = new Set(current);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  }, []);

  const resetKindFilter = useCallback((): void => {
    setKindFilter(new Set(defaultKinds));
  }, [defaultKinds]);

  const filteredLines = useMemo(() => {
    return lines.filter((line) => kindFilter.has(line.source_kind) && lineMatchesSearch(line, search));
  }, [kindFilter, lines, search]);

  const groupedLines = useMemo((): RepAgencyLineGroup[] => {
    const groups = new Map<string, CommissionRunLineRow[]>();
    for (const line of filteredLines) {
      const label = repAgencyGroupLabel(line);
      const bucket = groups.get(label);
      if (bucket == null) {
        groups.set(label, [line]);
      } else {
        bucket.push(line);
      }
    }

    const groupLabels = [...groups.keys()].sort((left, right) =>
      compareNullableStrings(left, right),
    );
    if (sortKey === "rep" && sortDir === "desc") {
      groupLabels.reverse();
    }

    return groupLabels.map((label) => {
      const groupLines = [...(groups.get(label) ?? [])];
      groupLines.sort((left, right) => {
        const result = compareRunLines(left, right, sortKey);
        return sortDir === "asc" ? result : -result;
      });
      const repAgencyId = groupLines.find((line) => line.rep_agency_id != null)?.rep_agency_id ?? null;
      return { label, repAgencyId, lines: groupLines };
    });
  }, [filteredLines, sortDir, sortKey]);

  const visibleLineCount = useMemo(
    () => groupedLines.reduce((sum, group) => sum + group.lines.length, 0),
    [groupedLines],
  );

  const hasActiveFilters = search.trim() !== "" || !kindFilterAtDefault;

  function toggleSort(nextKey: RunLineSortKey): void {
    if (sortKey === nextKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDir(
      nextKey === "basis" || nextKey === "rate" || nextKey === "commission" ? "desc" : "asc",
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor={searchInputId}>Search</Label>
            <Input
              id={searchInputId}
              type="search"
              className="mt-1"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Invoice number, end user, or rep"
            />
          </div>
          <KindCountPills
            title="Line kind totals"
            options={kindOptions}
            counts={kindCounts}
            selected={kindFilter}
            onToggle={toggleKindFilter}
            onReset={resetKindFilter}
            showReset={!kindFilterAtDefault}
          />
        </CardBody>
      </Card>

      <HubViewportCard>
        <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>{title}</CardTitle>
          {visibleLineCount > 0 ? (
            <span className="text-sm font-medium tabular-nums text-slate-700">
              Subtotal ({visibleLineCount.toLocaleString()}{" "}
              {visibleLineCount === 1 ? "line" : "lines"}) {money(sumLineCommissionAmount(filteredLines))}
            </span>
          ) : null}
        </CardHeader>
        <CardBody className="flex min-h-0 flex-1 flex-col p-0">
          <div className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-100">
            <table className="w-full min-w-[56rem] border-collapse text-sm">
              <thead>
                <tr>
                  <SortableTableHeader
                    label="Rep"
                    sortKey="rep"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className={RUN_LINES_HEADER_CELL}
                  />
                  <SortableTableHeader
                    label="Kind"
                    sortKey="kind"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className={RUN_LINES_HEADER_CELL}
                  />
                  <SortableTableHeader
                    label="End user"
                    sortKey="end_user"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className={RUN_LINES_HEADER_CELL}
                  />
                  <SortableTableHeader
                    label="Invoice"
                    sortKey="invoice"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className={RUN_LINES_HEADER_CELL}
                  />
                  <SortableTableHeader
                    label="Basis"
                    sortKey="basis"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    align="right"
                    className={RUN_LINES_HEADER_CELL}
                  />
                  <SortableTableHeader
                    label="Rate"
                    sortKey="rate"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    align="right"
                    className={RUN_LINES_HEADER_CELL}
                  />
                  <SortableTableHeader
                    label="Commission"
                    sortKey="commission"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    align="right"
                    className={RUN_LINES_HEADER_CELL}
                  />
                </tr>
              </thead>
              <tbody>
                {visibleLineCount === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-2 py-6 text-center text-slate-600">
                      {hasActiveFilters ? "No lines match these filters." : "No lines."}
                    </td>
                  </tr>
                ) : (
                  groupedLines.map((group) => (
                    <Fragment key={group.label}>
                      {group.lines.map((line) => {
                        const invoicePeek = runLineInvoicePeekFromRow(line);
                        return (
                          <tr key={line.id} className="border-b border-slate-100">
                            <td className="px-2 py-2">
                              <RepAgencyDetailLink
                                repAgencyId={line.rep_agency_id}
                                repAgencyName={line.rep_agency_name}
                              />
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-slate-800">{line.source_kind}</td>
                            <td className="max-w-[16rem] px-2 py-2">
                              <EndUserDetailLink
                                block
                                fsAccountId={line.end_user_fs_account_id}
                                endUserName={line.end_user_name}
                              />
                            </td>
                            <td className="whitespace-nowrap px-2 py-2">
                              <div className="flex items-center gap-1.5">
                                <InvoiceDetailLink
                                  invoiceMvId={line.invoice_mv_id}
                                  invoiceNumber={line.invoice_number}
                                  peek={invoicePeek}
                                />
                                {line.qb_line_total_mismatch ? (
                                  <QboMvMismatchBadge
                                    megaventoryTotal={invoicePeek?.total_amount ?? line.total_amount}
                                    quickbooksTotal={line.qb_compared_total}
                                    reason={line.qb_line_total_mismatch_reason}
                                  />
                                ) : null}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-800">
                              {money(line.basis_amount)}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-800">
                              {line.effective_rate == null
                                ? "—"
                                : `${(Number(line.effective_rate) * 100).toFixed(2)}%`}
                            </td>
                            <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-800">
                              {money(line.commission_amount)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr key={`subtotal-${group.label}`} className={RUN_LINE_SUBTOTAL_ROW_CLASS}>
                        <td className="px-2 py-2">
                          <span className="text-slate-900">{group.label}</span>
                          <span className="ml-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                            Subtotal
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-slate-600">
                          {group.lines.length.toLocaleString()}{" "}
                          {group.lines.length === 1 ? "line" : "lines"}
                        </td>
                        <td className="px-2 py-2" colSpan={2} />
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {money(sumLineBasisAmount(group.lines))}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-500">
                          —
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {money(sumLineCommissionAmount(group.lines))}
                        </td>
                      </tr>
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </HubViewportCard>
    </div>
  );
}

const PREVIEW_USER_SELECT_CLASS = cn(
  "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
  "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
);

function SendStatementPreviewModal({
  runId,
  onCancel,
  onSent,
}: {
  runId: number;
  onCancel: () => void;
  onSent: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState("");
  const usersQuery = useQuery({
    queryKey: ["commissions", "statement-preview-users"],
    queryFn: listCommissionStatementPreviewUsers,
  });
  const sendMutation = useMutation({
    mutationFn: () => sendCommissionRunStatementPreview(runId, userId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "run", runId] });
      onSent(
        `Sent ${data.statementEmailCount} statement preview email(s) and a recipient summary to ${data.previewEmail}.`,
      );
    },
  });

  const options = usersQuery.data?.users ?? [];
  const selectedUser = options.find((user) => user.id === userId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !sendMutation.isPending) {
          onCancel();
        }
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Send statement preview</h2>
          <p className="mt-1 text-sm text-slate-600">
            Emails every statement for this run to the selected Hub user, plus a separate summary of rep
            agencies and the recipients a real send would use. Statement rows are not marked sent.
          </p>
        </div>
        <div className="px-6 py-4">
          <Label htmlFor="statement-preview-user">Send preview to</Label>
          <select
            id="statement-preview-user"
            className={PREVIEW_USER_SELECT_CLASS}
            value={userId}
            disabled={usersQuery.isLoading || sendMutation.isPending}
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="">Select a user…</option>
            {options.map((user) => (
              <option key={user.id} value={user.id}>
                {user.display_name}
                {user.email != null ? ` (${user.email})` : ""}
              </option>
            ))}
          </select>
          {selectedUser?.email == null && userId !== "" ? (
            <p className="mt-2 text-sm text-amber-800">This user has no email on file.</p>
          ) : null}
          {sendMutation.isError ? (
            <p className="mt-2 text-sm text-red-700">
              {sendMutation.error instanceof Error ? sendMutation.error.message : "Send failed."}
            </p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <Button type="button" variant="secondary" disabled={sendMutation.isPending} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              sendMutation.isPending ||
              userId === "" ||
              selectedUser?.email == null ||
              usersQuery.isLoading
            }
            onClick={() => sendMutation.mutate()}
          >
            Send preview
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CommissionRunDetailPage() {
  const params = useParams();
  const runId = Number(params.id);
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [showBacktest, setShowBacktest] = useState(false);
  const [showStatementPreviewModal, setShowStatementPreviewModal] = useState(false);

  const runQuery = useQuery({
    queryKey: ["commissions", "run", runId],
    queryFn: () => getCommissionRun(runId),
    enabled: Number.isFinite(runId),
    refetchInterval: (query) => (query.state.data?.row.locked_at == null ? 60_000 : false),
  });

  const linesQuery = useQuery({
    queryKey: ["commissions", "run-lines", runId],
    queryFn: () => listCommissionRunLines(runId),
    enabled: Number.isFinite(runId),
    refetchInterval: () => {
      const locked = runQuery.data?.row.locked_at != null;
      return locked ? false : 60_000;
    },
  });

  const excludedQuery = useQuery({
    queryKey: ["commissions", "run-excluded", runId],
    queryFn: () => listCommissionRunExcludedItems(runId),
    enabled: Number.isFinite(runId) && runQuery.data?.row.locked_at != null,
    refetchInterval: 60_000,
  });

  const backtestQuery = useQuery({
    queryKey: ["commissions", "backtest", runQuery.data?.row.run_month],
    queryFn: () => getCommissionBacktest(String(runQuery.data?.row.run_month)),
    enabled: showBacktest && runQuery.data?.row.run_month != null,
  });

  const rulesQuery = useQuery({
    queryKey: commissionsRulesQueryKey,
    queryFn: getCommissionRules,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });
  const cutoverDryRun = rulesQuery.data?.cutoverDryRun ?? rulesQuery.data?.statementsDryRun ?? false;
  const lockHoverHint =
    "Lock freezes commission and bonus lines at current totals for review. Lines stop updating from live data.";
  const sendStatementsHoverHint = cutoverDryRun
    ? "Creates statement rows in the hub only — no Freshdesk email to reps during cutover dry-run. Requires all sign-offs and a statement preview first."
    : "Creates statement rows for each rep and emails contacts tagged commission statement via Freshdesk. Requires all sign-offs and a statement preview first.";
  const statementPreviewHoverHint =
    "Sends one email per statement to a Hub user you choose, plus a summary of real rep recipients. Does not mark statements sent or email reps. You can resend preview any time.";
  const vendorInvoicesHoverHint = cutoverDryRun
    ? "Would create QuickBooks vendor bills for rep payouts. Cutover dry-run — no bills are posted yet. Run paid status will update automatically when linked vendor invoices are paid in QuickBooks Online."
    : "Create QuickBooks vendor bills for rep payouts. Not yet implemented — manual bills for now. Run paid status will update automatically when linked vendor invoices are paid in QuickBooks Online.";

  const lockMutation = useMutation({
    mutationFn: () => lockCommissionRun(runId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "run", runId] });
      void queryClient.invalidateQueries({ queryKey: ["commissions", "run-lines", runId] });
      void queryClient.invalidateQueries({ queryKey: ["commissions", "run-excluded", runId] });
      void queryClient.invalidateQueries({ queryKey: ["commissions", "runs"] });
    },
  });

  const approveRoleMutation = useMutation({
    mutationFn: (role: Parameters<typeof approveCommissionRunRole>[1]) =>
      approveCommissionRunRole(runId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "run", runId] });
      void queryClient.invalidateQueries({ queryKey: ["commissions", "runs"] });
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => sendCommissionRunStatements(runId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "run", runId] });
    },
  });

  const vendorInvoicesMutation = useMutation({
    mutationFn: () => generateCommissionRunVendorInvoices(runId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["commissions", "run", runId] });
      if (data.dryRun && data.message != null) {
        showToast({ message: data.message, tone: "info" });
      } else if (data.message != null) {
        showToast({ message: data.message, tone: "success" });
      }
    },
  });

  const run = runQuery.data?.row;
  const live = runQuery.data?.live;
  const isLocked = run?.locked_at != null;
  const displayBase = isLocked ? run?.total_base : (live?.totals.totalBase ?? run?.total_base);
  const displayBonus = isLocked ? run?.total_bonus : (live?.totals.totalBonus ?? run?.total_bonus);
  const displayStocking = isLocked
    ? run?.total_stocking
    : (live?.totals.totalStocking ?? run?.total_stocking);
  const liveValidations = live?.validations ?? [];
  const allLines = linesQuery.data?.rows ?? [];
  const excludedLines = excludedQuery.data?.rows ?? [];
  const commissionLines = allLines.filter(
    (line) => COMMISSION_LINE_SOURCE_KINDS.has(line.source_kind) && !isReplessCommissionLine(line),
  );
  const bonusLines = allLines.filter((line) => BONUS_LINE_SOURCE_KINDS.has(line.source_kind));
  const runBreadcrumbLabel =
    run?.run_month != null ? commissionRunTitle(run.run_month) : "Commission run";
  const runMonthLongLabel =
    run?.run_month != null ? formatCommissionRunMonthLong(run.run_month) : null;
  const fullySignedOff = run != null ? isCommissionRunFullySignedOff(run) : false;
  const canSendStatementPreview =
    isLocked || run?.status === "approved" || run?.status === "paid";
  const canSendStatements =
    fullySignedOff &&
    run?.statement_preview_sent_at != null &&
    (run?.status === "approved" || run?.status === "paid");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <PageTitleWithHelp
            hoverHintAriaLabel="Commission run help"
            hoverHintContent={
              <>
                <p className="mb-2">
                  Lines update live from paid invoices, POS, and manual inputs until you lock the run. Lock
                  freezes the snapshot, then inside sales, operations, and executive sign off before invoices,
                  preview, and statements.
                </p>
                {runMonthLongLabel != null ? (
                  <p>
                    Payment month {runMonthLongLabel} (
                    {run?.run_month != null ? commissionRunMonthKey(run.run_month) : ""}): customer payments
                    in that calendar month.
                  </p>
                ) : null}
              </>
            }
            breadcrumbLabel={runBreadcrumbLabel}
          >
            {run?.run_month != null ? commissionRunTitle(run.run_month) : "…"}
          </PageTitleWithHelp>
        </div>
        <Link to="/commissions/runs" className="text-sm text-brand-700 hover:underline">
          Back to runs
        </Link>
      </div>

      {run != null ? (
        <CommissionRunWorkflowStatus
          run={run}
          approveRolePending={approveRoleMutation.isPending}
          onApproveRole={(role) => approveRoleMutation.mutate(role)}
        />
      ) : null}

      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <HoverHintTrigger
            trigger={
              <Button
                type="button"
                onClick={() => {
                  if (
                    !window.confirm(
                      "Lock this run? Commission and bonus lines will stop updating and the current totals will be frozen for review.",
                    )
                  ) {
                    return;
                  }
                  lockMutation.mutate();
                }}
                disabled={
                  lockMutation.isPending ||
                  isLocked ||
                  run?.status === "approved" ||
                  run?.status === "paid"
                }
              >
                Lock
              </Button>
            }
          >
            {lockHoverHint}
          </HoverHintTrigger>
          <HoverHintTrigger
            trigger={
              <Button
                type="button"
                onClick={() => sendMutation.mutate()}
                disabled={sendMutation.isPending || !canSendStatements}
              >
                {cutoverDryRun ? "Generate statements" : "Send statements"}
              </Button>
            }
          >
            {sendStatementsHoverHint}
          </HoverHintTrigger>
          <HoverHintTrigger
            trigger={
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowStatementPreviewModal(true)}
                disabled={!canSendStatementPreview}
              >
                Send statement preview
              </Button>
            }
          >
            {statementPreviewHoverHint}
          </HoverHintTrigger>
          <HoverHintTrigger
            trigger={
              <Button
                type="button"
                onClick={() => vendorInvoicesMutation.mutate()}
                disabled={vendorInvoicesMutation.isPending || !fullySignedOff}
              >
                Generate vendor invoices
              </Button>
            }
          >
            {vendorInvoicesHoverHint}
          </HoverHintTrigger>
          <Button type="button" variant="secondary" onClick={() => setShowBacktest(true)}>
            Backtest vs spreadsheet
          </Button>
        </div>
        {lockMutation.isError ||
        approveRoleMutation.isError ||
        sendMutation.isError ||
        vendorInvoicesMutation.isError ? (
          <p className="text-sm text-red-700">
            {(lockMutation.error ||
              approveRoleMutation.error ||
              sendMutation.error ||
              vendorInvoicesMutation.error) instanceof Error
              ? ((
                  lockMutation.error ||
                  approveRoleMutation.error ||
                  sendMutation.error ||
                  vendorInvoicesMutation.error
                ) as Error).message
              : "Action failed."}
          </p>
        ) : null}
        {isLocked ? (
          <p className="text-sm text-slate-700">
            Locked {run?.locked_at != null ? new Date(run.locked_at).toLocaleString() : ""}
            {run?.locked_by != null ? ` by ${run.locked_by}` : ""}. Lines are frozen; excluded items below
            show new qualifying activity since lock.
          </p>
        ) : (
          <p className="text-sm text-slate-600">
            Lines refresh automatically from current data. Lock when ready to freeze for review.
          </p>
        )}
        {sendMutation.data != null ? (
          <p className="text-sm text-slate-700">
            {sendMutation.data.dryRun ? "Generated (dry run) " : "Sent "}
            {sendMutation.data.results.filter((result) => result.ok).length} /{" "}
            {sendMutation.data.results.length} statements.
            {sendMutation.data.dryRun
              ? " No Freshdesk email was sent — statement rows only."
              : null}
          </p>
        ) : null}

        {runQuery.isLoading ? <p className="text-sm text-slate-600">Loading…</p> : null}

        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>Commission</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3 text-sm">
              <div>
                <div className="text-slate-500">Status</div>
                <div className="font-medium">
                  {run?.status ?? "—"}
                  {isLocked ? (
                    <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-slate-700">
                      Locked
                    </span>
                  ) : null}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Base commission</div>
                <div className="text-xl font-semibold tabular-nums">
                  {run != null ? money(displayBase) : "—"}
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Bonus</CardTitle>
            </CardHeader>
            <CardBody className="text-sm">
              <div className="text-slate-500">Bonus total</div>
              <div className="text-xl font-semibold tabular-nums">
                {run != null ? money(displayBonus) : "—"}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Stocking</CardTitle>
            </CardHeader>
            <CardBody className="text-sm">
              <div className="text-slate-500">Regional stocking</div>
              <div className="text-xl font-semibold tabular-nums">
                {run != null ? money(displayStocking) : "—"}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {(liveValidations.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Validations</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            {liveValidations.map((validation, index) => (
              <div
                key={`${validation.code}-${index}`}
                className={
                  validation.severity === "error"
                    ? "rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                    : "rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                }
              >
                <strong>{validation.severity}</strong>: {validation.message}
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {showBacktest ? (
        <Card>
          <CardHeader>
            <CardTitle>Backtest differences</CardTitle>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            {backtestQuery.isLoading ? <p className="text-sm text-slate-600">Comparing…</p> : null}
            {backtestQuery.data != null ? (
              <>
                <p className="mb-3 text-sm text-slate-700">
                  Hub base {money(backtestQuery.data.hubTotals.totalBase)} / sheet base{" "}
                  {money(backtestQuery.data.sheetTotals.totalBase)} —{" "}
                  {backtestQuery.data.clean ? "clean match" : `${backtestQuery.data.diffs.length} diffs`}
                </p>
                <HubDataTable
                  fillViewport={false}
                  columns={BACKTEST_DIFF_COLUMNS}
                  rows={backtestQuery.data.diffs}
                  rowKey={(row) =>
                    `${row.repAgencyName}-${row.invoiceNumber ?? ""}-${row.reason}-${String(row.delta)}`
                  }
                  empty="No differences."
                />
              </>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {isLocked ? (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-900">Excluded items</h2>
          <p className="text-sm text-slate-600">
            Qualifying invoices, POS lines, and manual entries that were not in the frozen snapshot
            at lock time.
          </p>
          {excludedQuery.isLoading ? (
            <p className="text-sm text-slate-600">Loading…</p>
          ) : excludedLines.length === 0 ? (
            <Card>
              <CardBody>
                <p className="text-sm text-slate-600">No excluded items since lock.</p>
              </CardBody>
            </Card>
          ) : (
            <CommissionRunLinesTable
              title="Excluded items"
              lines={excludedLines}
              kindOptions={[...COMMISSION_KIND_OPTIONS, ...BONUS_KIND_OPTIONS]}
              searchInputId="excluded-run-lines-search"
            />
          )}
        </section>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Commission lines</h2>
        {linesQuery.isLoading ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : (
          <CommissionRunLinesTable
            title="Commission lines"
            lines={commissionLines}
            kindOptions={COMMISSION_KIND_OPTIONS}
            searchInputId="commission-run-lines-search"
          />
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Bonus lines</h2>
        {linesQuery.isLoading ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : (
          <CommissionRunLinesTable
            title="Bonus lines"
            lines={bonusLines}
            kindOptions={BONUS_KIND_OPTIONS}
            searchInputId="bonus-run-lines-search"
          />
        )}
      </section>

      {showStatementPreviewModal ? (
        <SendStatementPreviewModal
          runId={runId}
          onCancel={() => setShowStatementPreviewModal(false)}
          onSent={(message) => {
            setShowStatementPreviewModal(false);
            showToast({ message, tone: "success" });
          }}
        />
      ) : null}
    </div>
  );
}
